import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useFormattingContext } from '../../../../../../FormattingProvider/FormattingContext'
import FastAvatar from '../../../../../../../molecules/FastAvatar'

const useStyles = makeStyles(() => ({
  comment: {
    display: 'flex',
    gap: 'var(--dim-2)',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    gap: 'var(--dim-2)',
    alignItems: 'center'
  },
  avatarWrapper: {
    flexShrink: 0
  },
  userAvatar: {
    border: 'var(--border-neutral-thin)'
  },
  userInfo: {
    display: 'flex',
    gap: 'var(--dim-4)',
    alignItems: 'center'
  },
  userName: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-neutral-900)',
    margin: 0
  },
  date: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-600)',
    margin: 0
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-2)'
  },
  markdown: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-900)',
    fontWeight: 'var(--font-weight-light)',
    lineHeight: 1.5,
    '& > *:first-child': {
      marginTop: 0
    },
    '& > *:last-child': {
      marginBottom: 0
    },
    '& p': {
      margin: '0.5em 0'
    },
    '& ul, & ol': {
      marginLeft: '1.5em',
      margin: '0.5em 0'
    },
    '& code': {
      background: 'var(--color-neutral-100)',
      padding: '0.125em 0.25em',
      borderRadius: 'var(--border-radius-sm)',
      fontSize: '0.9em'
    },
    '& pre': {
      background: 'var(--color-neutral-100)',
      padding: 'var(--dim-2)',
      borderRadius: 'var(--border-radius-sm)',
      overflow: 'auto',
      '& code': {
        background: 'none',
        padding: 0
      }
    },
    '& blockquote': {
      borderLeft: '3px solid var(--color-neutral-400)',
      paddingLeft: 'var(--dim-3)',
      margin: '0.5em 0',
      color: 'var(--color-neutral-700)'
    }
  }
}))

function Comment ({ comment, currentUserId, onToggleReaction }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()

  const formattedDate = useMemo(() => {
    return formatter(comment.createdDate, '@MMM D, YYYY')
  }, [comment.createdDate, formatter])

  const userAbbreviation = useMemo(() => {
    if (!comment.userName) return ''
    return comment.userName.charAt(0)
  }, [comment.userName])

  return (
    <div className={classes.comment}>
      <div className={classes.header}>
        <div className={classes.avatarWrapper}>
          <FastAvatar
            size='sm'
            avatarUrl={comment.userAvatar}
            avatarType={comment.avatarType || 'media'}
            abbreviation={userAbbreviation}
            className={classes.userAvatar}
          />
        </div>
        <div className={classes.userInfo}>
          <p className={classes.userName}>{comment.userName}</p>
          <p className={classes.date}>{formattedDate}</p>
        </div>
      </div>
      <div className={classes.body}>
        <ReactMarkdown className={classes.markdown} remarkPlugins={[remarkGfm]}>
          {comment.text}
        </ReactMarkdown>
      </div>
    </div>
  )
}

Comment.propTypes = {
  comment: PropTypes.shape({
    commentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string,
    createdDate: PropTypes.string,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userName: PropTypes.string,
    userAvatar: PropTypes.string,
    avatarType: PropTypes.oneOf(['media', 'override', 'resource', 'public']),
    reactions: PropTypes.arrayOf(
      PropTypes.shape({
        emoji: PropTypes.string,
        count: PropTypes.number,
        userIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
      })
    )
  }),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggleReaction: PropTypes.func
}

export default Comment
